import { invoke } from "@tauri-apps/api/core";
import { translate } from "@/i18n";
import { desktopVoiceMessages } from "@/i18n/messages/desktop-voice";
import { prewarmAgentSession } from "@/lib/agent-prewarm";
import { isTauri } from "@/lib/is-tauri";
import { frameTaskChat } from "@/lib/task-chat-kind";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import { useFramesStore } from "@/stores/frames-store";
import {
  useInputBarTarget,
  type InputBarRoutingIntent,
} from "@/stores/input-bar-target-store";
import { useProjectStore } from "@/stores/project-store";
import { useWorkspaceConversationOverlay } from "@/stores/workspace-conversation-overlay-store";
import { useFloatingFrameStore } from "./floating-frame-store";
import { announceVoiceChatCommand } from "./voice-command-feedback-store";
import { debugToolsEnabled } from "@/lib/release-channel";

export type VoiceTogglePayload = {
  startNewConversation?: boolean;
  /** Shift joined the held voice chord after it had started listening. */
  extendsHeldChord?: boolean;
};

export type VoiceNewChatSegmentIdentity = {
  captureId?: string;
  segmentId?: number;
};

export type VoiceNewChatDispatchIntent = {
  startNewConversation: true;
  clientRequestId: string;
};

type NativeVoiceNewChatRoute = {
  clientRequestId: string;
  agentId: string | null;
  frameId: string | null;
  expiresAt: number;
};

let speculativeIdentity: VoiceNewChatSegmentIdentity | null = null;
let locallyArmedIntentId: string | null = null;
let locallyArmedRouteKind: "committed" | "provisional" | null = null;
/** The bar's route before this window armed a voice New chat; a cancelled
 *  provisional recording puts it back. */
let routeBeforeArm: InputBarRoutingIntent | null = null;

export function traceVoiceNewChatRouting(
  event: string,
  properties: Record<string, unknown> = {},
): void {
  if (!debugToolsEnabled()) return;
  void invoke("dbg_log", {
    msg: `[voice-new-chat] ${event} ${JSON.stringify(properties)}`,
  }).catch(() => {});
}

function sameSegment(
  intent: VoiceNewChatSegmentIdentity,
  identity: VoiceNewChatSegmentIdentity,
): boolean {
  return (
    intent.captureId === identity.captureId &&
    intent.segmentId === identity.segmentId
  );
}

function startNewChatPrewarm(force = false): void {
  prewarmAgentSession("new_chat", {
    ...(force ? { force: true } : {}),
    startNewConversation: true,
  });
}

function armNativeVoiceNewChatRoute(route: NativeVoiceNewChatRoute): void {
  void invoke("arm_voice_new_chat_route", { route }).catch(() => {});
}

function clearNativeVoiceNewChatRoute(
  clientRequestId?: string | null,
  requireSuccess = false,
): Promise<void> {
  if (requireSuccess && !isTauri()) return Promise.resolve();
  const cleared = invoke<void>("clear_voice_new_chat_route", {
    clientRequestId: clientRequestId ?? null,
  });
  return requireSuccess ? cleared : cleared.catch(() => {});
}

function persistNativeVoiceNewChatRoute(
  state: ReturnType<typeof useInputBarTarget.getState>,
): void {
  if (!state.intentId) return;
  armNativeVoiceNewChatRoute({
    clientRequestId: state.intentId,
    agentId: state.newConversationAgentId,
    frameId: state.newConversationFrameId,
    expiresAt: 0,
  });
}

/**
 * Shared resolution for "which conversation frame is the user's dictation
 * currently pointed at" — used both to decide where a spoken *new*
 * conversation should be routed (`requireCanStartNewConversation: true`,
 * where a frame explicitly marked `conversationCanStartNewConversation:
 * false` must be skipped) and, via `activeConversationComposerFrameId`,
 * to find the already-open composer whose draft should be cleared once a
 * dictated message into an *existing* conversation is accepted. That
 * second case has nothing to do with starting a new conversation — a
 * frame that disallows starting new conversations from itself (e.g. one
 * already mid-conversation via the "Focus"/conversation-columns layout)
 * can still be exactly the composer the draft needs clearing from, and
 * requiring the flag there was a real bug: it made every dictated reply
 * into a focused conversation permanently fail to clear its input bar.
 */
function resolveActiveConversationFrameId(
  requireCanStartNewConversation: boolean,
): string | null {
  const projectId = useProjectStore.getState().currentProject?.id;
  const overlay = useWorkspaceConversationOverlay.getState().overlay;
  const eligible = (metadata: Record<string, unknown>) =>
    frameTaskChat(metadata) === null &&
    (!requireCanStartNewConversation ||
      metadata.conversationCanStartNewConversation !== false);
  if (
    overlay &&
    overlay.project.id === projectId &&
    overlay.frame.frame_type === "conversation" &&
    overlay.frame.metadata.hidden !== true &&
    eligible(overlay.frame.metadata)
  )
    return overlay.frame.id;
  const presentation = usePresentationStore.getState().presentation;
  const focusedFrameId =
    presentation?.variant === "focus"
      ? presentation.frameIds[presentation.activeIndex]
      : null;
  const { selectedFrameId } = useSelectionStore.getState();
  for (const frameId of [selectedFrameId, focusedFrameId]) {
    if (!frameId) continue;
    const floating = useFloatingFrameStore.getState().frames[frameId];
    const frame =
      useFramesStore.getState().framesById[frameId] ??
      (floating && floating.projectId === projectId
        ? floating.frame
        : undefined);
    if (
      frame?.frameType === "conversation" &&
      frame.metadata.hidden !== true &&
      eligible(frame.metadata)
    ) {
      return frameId;
    }
  }
  return null;
}

export function activeConversationFrameId(): string | null {
  return resolveActiveConversationFrameId(true);
}

/** See `resolveActiveConversationFrameId` — does not require the frame to
 *  allow starting a *new* conversation, since this answers a different
 *  question: which already-open composer is the user's dictation for. */
export function activeConversationComposerFrameId(): string | null {
  return resolveActiveConversationFrameId(false);
}

export function speculateVoiceNewChatRouting(
  identity: VoiceNewChatSegmentIdentity,
): void {
  if (useInputBarTarget.getState().newConversationSource === "voice") return;
  if (speculativeIdentity && sameSegment(speculativeIdentity, identity)) {
    return;
  }
  speculativeIdentity = identity;
  startNewChatPrewarm(true);
}

export function clearSpeculativeVoiceNewChatRouting(
  identity?: VoiceNewChatSegmentIdentity,
): void {
  if (
    identity &&
    speculativeIdentity &&
    !sameSegment(speculativeIdentity, identity)
  ) {
    return;
  }
  speculativeIdentity = null;
}

function armVoiceNewChatRoute(
  identity: VoiceNewChatSegmentIdentity = {},
  routeKind: "committed" | "provisional",
): void {
  const state = useInputBarTarget.getState();
  if (state.startNewConversation && state.newConversationSource === "voice") {
    const effectiveRouteKind =
      locallyArmedRouteKind === "committed" || routeKind === "committed"
        ? "committed"
        : "provisional";
    speculativeIdentity = null;
    if (locallyArmedIntentId !== state.intentId) routeBeforeArm = null;
    locallyArmedIntentId = state.intentId;
    locallyArmedRouteKind = effectiveRouteKind;
    traceVoiceNewChatRouting("route_reused", {
      routeKind: effectiveRouteKind,
      intentId: state.intentId,
      frameId: state.newConversationFrameId,
    });
    persistNativeVoiceNewChatRoute(state);
    return;
  }
  const wasSpeculativelyPrewarmed =
    speculativeIdentity !== null && sameSegment(speculativeIdentity, identity);
  speculativeIdentity = null;
  routeBeforeArm = state.snapshotRoutingIntent();
  state.requestVoiceNewConversation(activeConversationFrameId());
  const armedState = useInputBarTarget.getState();
  locallyArmedIntentId = armedState.intentId;
  locallyArmedRouteKind = routeKind;
  traceVoiceNewChatRouting("route_armed", {
    routeKind,
    intentId: armedState.intentId,
    frameId: armedState.newConversationFrameId,
    prewarmed: wasSpeculativelyPrewarmed,
  });
  persistNativeVoiceNewChatRoute(armedState);
  if (!wasSpeculativelyPrewarmed) startNewChatPrewarm(true);
}

export function armPendingVoiceNewChatRouting(
  identity: VoiceNewChatSegmentIdentity = {},
): void {
  armVoiceNewChatRoute(identity, "committed");
}

/** Live Talk keeps listening and its next turn goes to a new chat: said
 *  ("new chat"), chorded, or asked of Denker in any words. */
export function startLiveTalkNewChat(
  identity: VoiceNewChatSegmentIdentity = {},
): void {
  armPendingVoiceNewChatRouting(identity);
  announceVoiceChatCommand(translate(desktopVoiceMessages, "commandNewChat"));
}

/**
 * Arms the new-chat route for a recording about to start, or, when the
 * new-chat chord extends the held voice chord, for the one it just started.
 */
export function armVoiceNewChatRouting(
  payload: VoiceTogglePayload,
  status: "idle" | "recording" | "processing" | "error",
  unavailable: boolean,
): boolean {
  const routable = payload.extendsHeldChord
    ? status === "recording"
    : status === "idle" || status === "error";
  if (!unavailable && payload.startNewConversation === true && routable) {
    armVoiceNewChatRoute({}, "provisional");
    return true;
  }
  return false;
}

export async function consumePendingVoiceNewChatIntent(): Promise<VoiceNewChatDispatchIntent | null> {
  const state = useInputBarTarget.getState();
  if (
    state.startNewConversation &&
    state.newConversationSource === "voice" &&
    state.intentId
  ) {
    traceVoiceNewChatRouting("route_consumed_for_dispatch", {
      intentId: state.intentId,
      frameId: state.newConversationFrameId,
      routeKind: locallyArmedRouteKind,
    });
    return {
      startNewConversation: true,
      clientRequestId: state.intentId,
    };
  }

  if (state.startNewConversation && state.newConversationSource === "input") {
    clearNativeVoiceNewChatRoute();
    return null;
  }

  const nativeRoute = await invoke<NativeVoiceNewChatRoute | null>(
    "read_voice_new_chat_route",
  ).catch(() => null);
  if (!nativeRoute) {
    traceVoiceNewChatRouting("route_unavailable", {
      startNewConversation: state.startNewConversation,
      source: state.newConversationSource,
      intentId: state.intentId,
    });
    return null;
  }
  useInputBarTarget.setState({
    target: null,
    startNewConversation: true,
    newConversationSource: "voice",
    intentId: nativeRoute.clientRequestId,
    newConversationAgentId: nativeRoute.agentId,
    newConversationFrameId: nativeRoute.frameId,
    claimedIntentId: null,
    claimOwnerId: null,
    claimExpiresAt: null,
  });
  locallyArmedIntentId = nativeRoute.clientRequestId;
  locallyArmedRouteKind = "committed";
  traceVoiceNewChatRouting("native_route_restored", {
    intentId: nativeRoute.clientRequestId,
    frameId: nativeRoute.frameId,
  });
  return {
    startNewConversation: true,
    clientRequestId: nativeRoute.clientRequestId,
  };
}

export function abandonProvisionalVoiceNewChatRouting(reason: string): void {
  speculativeIdentity = null;
  const state = useInputBarTarget.getState();
  const ownsCurrentRoute =
    !!locallyArmedIntentId &&
    state.newConversationSource === "voice" &&
    state.intentId === locallyArmedIntentId;
  if (locallyArmedRouteKind === "provisional" && ownsCurrentRoute) {
    traceVoiceNewChatRouting("provisional_route_abandoned", {
      reason,
      intentId: locallyArmedIntentId,
    });
    if (routeBeforeArm) state.restoreRoutingIntent(routeBeforeArm);
    else state.clearTarget();
    clearNativeVoiceNewChatRoute(locallyArmedIntentId);
    locallyArmedIntentId = null;
    locallyArmedRouteKind = null;
    routeBeforeArm = null;
    return;
  }
  traceVoiceNewChatRouting("committed_route_preserved", {
    reason,
    intentId: ownsCurrentRoute ? locallyArmedIntentId : null,
  });
  if (!ownsCurrentRoute) {
    locallyArmedIntentId = null;
    locallyArmedRouteKind = null;
    routeBeforeArm = null;
  }
}

export function consumePendingVoiceNewChatRouting(): boolean {
  const state = useInputBarTarget.getState();
  return (
    state.startNewConversation &&
    state.newConversationSource === "voice" &&
    !!state.intentId
  );
}

export function settlePendingVoiceNewChatIntent(clientRequestId: string): void {
  clearNativeVoiceNewChatRoute(clientRequestId);
  if (locallyArmedIntentId === clientRequestId) {
    locallyArmedIntentId = null;
    locallyArmedRouteKind = null;
    routeBeforeArm = null;
  }
  traceVoiceNewChatRouting("route_settled", { intentId: clientRequestId });
}

export function discardPendingNativeVoiceNewChatIntent(
  options: { requireSuccess?: boolean } = {},
): Promise<void> {
  const cleared = clearNativeVoiceNewChatRoute(
    undefined,
    options.requireSuccess,
  );
  locallyArmedIntentId = null;
  locallyArmedRouteKind = null;
  routeBeforeArm = null;
  return cleared;
}

export function clearPendingVoiceNewChatRouting(): void {
  speculativeIdentity = null;
  const state = useInputBarTarget.getState();
  if (state.newConversationSource === "voice" && state.intentId) {
    traceVoiceNewChatRouting("route_explicitly_cleared", {
      intentId: state.intentId,
    });
    const intentId = state.intentId;
    state.clearTarget();
    clearNativeVoiceNewChatRoute(intentId);
  } else if (locallyArmedIntentId) {
    clearNativeVoiceNewChatRoute(locallyArmedIntentId);
  }
  locallyArmedIntentId = null;
  locallyArmedRouteKind = null;
  routeBeforeArm = null;
}
